// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { useTemplateListQuery } from '../../../../queries/useConfigQueries';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { composeDraftBlurHandler } from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getAttributeListOptionLabel = (option) => option?.entity_name;
const isOptionEqualToValue = (option, value) =>
  String(option?.id) === String(value?.id);

const TemplateListFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  required = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  /**
   * for TemplateListFieldComponent data type value will be json, u will get what we are sending
   * {
   * "template_id":"uuid",
   * "entity_name":"sdfsd",
   * "template_version_number":"2",
   * "template_version_id":"uuid"
   * }
   */
  const { data: templateList = EMPTY_ARRAY } = useTemplateListQuery();

  const selectedOption = useMemo(() => {
    return (
      templateList.find(
        (option) => String(option?.id) === String(value?.template_id)
      ) || null
    );
  }, [templateList, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue) {
        onChange({
          template_id: newValue?.id,
          entity_name: newValue?.entity_name,
          template_version_number: newValue?.version_number,
          template_version_id: newValue?.version_id,
        });
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  const { onBlur, name } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleInputBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(onBlur, onDraftBlur)(event);
    },
    [onBlur, onDraftBlur]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        name={name}
        required={required}
        placeholder="Select template"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select template',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [
      attribute?.id,
      entityDetailsFormikForm,
      label,
      required,
      titleLabel,
      hasDraftChip,
      name,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={templateList}
      onChange={handleChange}
      onBlur={handleInputBlur}
      onClose={handleClose}
      noOptionsText="No template present."
      popupIcon={<DropdownArrow />}
      getOptionLabel={getAttributeListOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
};

TemplateListFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default TemplateListFieldComponent;
